import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { useAuth } from '../../components/Auth';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import EmailTemplatesPresentational from './EmailTemplatesPresentational';
import fetchEmailTemplates, { createEmailTemplate, deleteEmailTemplate, saveEmailTemplate } from '../../lib/fetch/emailTemplates';

function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [t] = useTranslation('EMAIL_TEMPLATES');
  const [{ token } = {}] = useAuth();
  const alert = useAlert();

  const [query, setQuery] = useQueryParams({
    search: StringParam,
    funnel_stage: StringParam,
    scenario: StringParam,
    language: StringParam,
    tone: StringParam,
    tag: StringParam,
    offset: NumberParam,
  });

  const {
    search,
    funnel_stage,
    scenario,
    language,
    tone,
    tag,
    offset = 0,
  } = query;

  useEffect(() => {
    const loadTemplates = async () => {
      if (!token) return;

      setIsLoading(true);
      const { error, data, headers } = await fetchEmailTemplates({
        token,
        search,
        funnel_stage,
        scenario,
        language,
        tone,
        tag,
        offset,
        limit: 10,
        order: 'updated_at',
        sort: 'desc',
      });

      if (error) {
        setTemplates([]);
        setTotal(0);
        setIsLoading(false);
        alert.error(t('COMMON:GENERIC_ERROR'));
        return;
      }

      setTemplates(data || []);
      setTotal(Number(headers['x-total-count'] || 0));
      setIsLoading(false);
    };

    loadTemplates();
  }, [alert, funnel_stage, language, offset, refreshKey, scenario, search, tag, t, token, tone]);

  const openCreateTemplate = () => {
    setSelectedTemplate(null);
    setEditorOpen(true);
  };

  const openEditTemplate = (template) => {
    setSelectedTemplate(template);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setSelectedTemplate(null);
  };

  const normalizePayload = (payload) => ({
    ...payload,
    tags: Array.isArray(payload.tags) ? payload.tags : [],
  });

  const onSaveTemplate = async (payload) => {
    setIsSaving(true);
    const data = normalizePayload(payload);

    const request = selectedTemplate?.id
      ? saveEmailTemplate({ token, id: selectedTemplate.id, data })
      : createEmailTemplate({ token, data });

    const { error } = await request;
    setIsSaving(false);

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return false;
    }

    alert.success(selectedTemplate?.id ? t('TEMPLATE_UPDATED') : t('TEMPLATE_SAVED'));
    setEditorOpen(false);
    setSelectedTemplate(null);
    setQuery({ offset: 0 });
    setRefreshKey(key => key + 1);
    return true;
  };

  const onDeleteTemplate = async (id) => {
    const { error } = await deleteEmailTemplate({ token, id });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    alert.success(t('TEMPLATE_DELETED'));
    setQuery({ offset: 0 });
    setRefreshKey(key => key + 1);
  };

  return (
    <EmailTemplatesPresentational
      templates={templates}
      total={total}
      query={query}
      onFilter={setQuery}
      onCreateTemplate={openCreateTemplate}
      onEditTemplate={openEditTemplate}
      onDeleteTemplate={onDeleteTemplate}
      selectedTemplate={selectedTemplate}
      editorOpen={editorOpen}
      onCloseEditor={closeEditor}
      onSaveTemplate={onSaveTemplate}
      isSaving={isSaving}
      isLoading={isLoading}
    />
  );
}

export default EmailTemplates;
